import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CardMedia, Input, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import SwitchToggle from "../components/SwitchToggle";
import {
    useGetBrandsQuery,
    useGetCategoriesQuery,
    useGetModelsQuery,
    useAddVariantMutation,
    useUpdateVariantMutation,
    useUploadMediaMutation
} from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
    variantData?: any;
}

export default function AddVariant({
    open,
    onClose,
    setOpen,
    variantData
}: ModalProps) {

    const [selectedBrand, setSelectedBrand] = useState<string>("");
    const [selectedModel, setSelectedModel] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
    const [name, setName] = useState<string>("");
    const [image, setImage] = useState<string>("");
    const [isPopular, setIsPopular] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});

    // Queries
    const { data: brandsData } = useGetBrandsQuery(
        { page: 1, limit: 100, type: selectedVehicleType || undefined },
        { skip: !selectedVehicleType }
    );
    const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, brandId: selectedBrand }, { skip: !selectedBrand });

    // Fetch models based on selected brand
    const { data: modelsData } = useGetModelsQuery(
        { page: 1, limit: 100, brandId: selectedBrand || undefined },
        { skip: !selectedBrand }
    );

    const [addVariant, { isLoading: isAdding }] = useAddVariantMutation();
    const [updateVariant, { isLoading: isUpdating }] = useUpdateVariantMutation();
    const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

    useEffect(() => {
        if (variantData && open) {
            setName(variantData.name || "");
            setSelectedBrand(variantData.brandId || variantData.brand?._id || "");
            setSelectedModel(variantData.modelId || variantData.model?._id || "");

            let catId: string = "";
            if (variantData.categoryId) {
                // If it comes as array or string, handle both but we need single string
                const temp = Array.isArray(variantData.categoryId) ? variantData.categoryId[0] : variantData.categoryId;
                catId = typeof temp === 'object' ? temp._id : temp;
            } else if (variantData.category) {
                // category might be populated object or array of objects
                const temp = Array.isArray(variantData.category) ? variantData.category[0] : variantData.category;
                catId = temp?._id || "";
            }
            setSelectedCategory(catId);
            const vType = variantData.type;
            setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
            setImage(variantData.image || "");
            setIsPopular(variantData.isPopular || false);
            setErrors({});
        } else if (!variantData && open) {
            // Reset form
            setName("");
            setSelectedBrand("");
            setSelectedModel("");
            setSelectedCategory("");
            setSelectedVehicleType("");
            setImage("");
            setIsPopular(false);
            setErrors({});
        }
    }, [variantData, open]);

    const handleBrandChange = (event: any) => {
        setSelectedBrand(event.target.value as string);
        setSelectedModel(""); // Reset model when brand changes
        setSelectedCategory(""); // Reset category when brand changes
        if (errors.brand) setErrors({ ...errors, brand: "" });
    };

    const handleModelChange = (event: any) => {
        setSelectedModel(event.target.value as string);
        if (errors.model) setErrors({ ...errors, model: "" });
    };

    const handleCategoryChange = (event: any) => {
        setSelectedCategory(event.target.value as string);
        if (errors.category) setErrors({ ...errors, category: "" });
    };

    const handleVehicleTypeChange = (event: any) => {
        const value = event.target.value as number;
        setSelectedVehicleType(value);
        setSelectedBrand(""); // Reset brand when type changes
        setSelectedModel(""); // Reset model when type changes
        setSelectedCategory(""); // Reset category when type changes
        if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
    };



    const handleNameChange = (e: any) => {
        if (e.target.value.length <= 40) {
            setName(e.target.value);
            if (errors.name) setErrors({ ...errors, name: "" });
        }
    };

    const handleValidation = () => {
        let tempErrors: any = {};
        let isValid = true;

        if (!name.trim()) {
            tempErrors.name = "Variant name is required / اسم المتغير مطلوب";
            isValid = false;
        }
        if (!selectedBrand) {
            tempErrors.brand = "Brand is required / العلامة التجارية مطلوبة";
            isValid = false;
        }
        if (!selectedModel) {
            tempErrors.model = "Model is required / الطراز مطلوب";
            isValid = false;
        }
        if (!selectedCategory) {
            tempErrors.category = "Category is required / الفئة مطلوبة";
            isValid = false;
        }
        if (!selectedVehicleType) {
            tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
            isValid = false;
        }
        if (!image) {
            tempErrors.image = "Image is required / الصورة مطلوبة";
            isValid = false;
        }
        if (name.length > 40) {
            tempErrors.name = "Variant name cannot exceed 40 characters / لا يمكن أن يتجاوز اسم المتغير 40 حرفًا";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            const file = files[0];
            if (!file.type.includes("image")) {
                toast.error("Please upload a valid image file / يرجى تحميل ملف صورة صالح");
                return;
            }

            const formData = new FormData();
            formData.append("file", file);

            try {
                const res = await uploadMedia(formData).unwrap();
                const uploadedUrl = res?.data?.url || res?.url || res?.data;
                if (uploadedUrl) {
                    setImage(uploadedUrl);
                    if (errors.image) setErrors({ ...errors, image: "" });
                    toast.success("Image uploaded successfully / تم تحميل الصورة بنجاح");
                }
            } catch (err: any) {
                toast.error("Failed to upload image / فشل تحميل الصورة");
            }
        }
    };

    const handleSubmit = async () => {
        if (!handleValidation()) return;

        const payload = {
            name,
            images: [image], // Changed to array of strings
            categoryId: selectedCategory, // Changed to single string
            brandId: selectedBrand,
            modelId: selectedModel,
            type: Number(selectedVehicleType),
            isPopular,
            order: 1,
            isActive: true, // Assuming isActive is true for new adding, or we should respect edit? 
            // User example has isActive: true. For edit, maybe we should preserve?
            // But edit usually passes id. Let's just follow example.
            isDeleted: false
        };

        // If editing, merge/preserve some properties if API requires or just send current state
        if (variantData?._id) {
            // For edit, user might not want to reset isActive if they disabled it.
            // But the example provided is a POST (add). 
            // Let's assume standard update behavior. The backend might ignore extra fields or update them.
            // If updateVariant uses PUT, it replaces. If PATCH, it merges.
            // Our service uses PUT. So we should probably include current status if editing.
            // But specific prompt just said "in add variant body will be like this".
            // I will stick to the prompt structure for both add/edit where relevant, 
            // but maybe keep isActive dynamic for edit if we had a toggle for it (we don't have isActive toggle in this modal, we have isPopular).
            // Actually, the main manage page has toggle for isActive.
            // I will just use payload as constructed.
        }

        try {
            if (variantData?._id) {
                // For update, we might need to adjust payload if backend handles PUT strictly.
                // But typically updates allow partial or full replacement.
                // Note: The 'images' field key change from 'image' to 'images' likely applies to update too.
                await updateVariant({ id: variantData._id, ...payload }).unwrap();
                toast.success("Variant updated successfully / تم تحديث المتغير بنجاح");
            } else {
                await addVariant(payload).unwrap();
                toast.success("Variant added successfully / تمت إضافة المتغير بنجاح");
            }
            setOpen(false);
        } catch (err: any) {
            toast.error(err?.data?.message || "Something went wrong / حدث خطأ ما");
        }
    };

    return (
        <Modal
            className="modal rejectReason_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>{variantData ? "Edit Variant / تعديل المتغير" : "Add Variant / إضافة متغير"}</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close / إغلاق"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="gap_p">
                            <div className="control_group w_100">
                                <label htmlFor="upload-image">
                                    Upload Image / تحميل الصورة<span className="req">*</span>
                                </label>

                                {isUploading ? (
                                    <div className="upload_image no_radius" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <CircularProgress size={24} />
                                    </div>
                                ) : image ? (
                                    <div className="upload_image no_radius">
                                        <div className="upload_image_holder">
                                            <figure>
                                                <CardMedia component="img" image={image} alt="Variant" />
                                            </figure>
                                            <CloseIcon
                                                className="remove_img"
                                                onClick={() => {
                                                    setImage("");
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <label
                                        className="upload_image no_radius"
                                        htmlFor="upload-image"
                                    >
                                        <Input
                                            sx={{ display: "none" }}
                                            id="upload-image"
                                            type="file"
                                            inputProps={{ accept: "image/png,image/jpeg" }}
                                            onChange={handleImageUpload}
                                            disabled={isUploading}
                                        />
                                        <span className="upload_image_holder">
                                            <figure className="add_img">
                                                <img
                                                    src="/static/images/placeholder.png"
                                                    alt="Upload / تحميل"
                                                />
                                                <span className="upload_img_icon">
                                                    <img
                                                        src="/static/images/edit2_icon.svg"
                                                        alt="Edit / تعديل"
                                                    />
                                                </span>
                                            </figure>
                                        </span>
                                    </label>
                                )}
                                {errors.image && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.image}</span>}
                            </div>

                            <div className="control_group w_100">
                                <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
                                <Select
                                    fullWidth
                                    hiddenLabel
                                    displayEmpty
                                    value={selectedVehicleType}
                                    onChange={handleVehicleTypeChange}
                                    renderValue={(selected) => {
                                        if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                                        return getVehicleTypeLabel(selected);
                                    }}
                                    error={!!errors.vehicleType}
                                >
                                    <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                                    {Object.keys(VEHICLE_TYPE)
                                        .filter(k => isNaN(Number(k)))
                                        .map((key) => (
                                            <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                                                {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                                            </MenuItem>
                                        ))}
                                </Select>
                                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
                            </div>

                            <div className="control_group w_50">
                                <label htmlFor="variant-name">Variant Name / اسم المتغير</label>
                                <TextField
                                    id="variant-name"
                                    placeholder="Enter Name / أدخل الاسم"
                                    fullWidth
                                    hiddenLabel
                                    value={name}
                                    onChange={handleNameChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    inputProps={{ maxLength: 40 }}
                                />
                            </div>
                            <div className="control_group w_50">
                                <label>Brand / العلامة التجارية</label>
                                <Select
                                    fullWidth
                                    hiddenLabel
                                    value={selectedBrand}
                                    onChange={handleBrandChange}
                                    displayEmpty
                                    disabled={!selectedVehicleType}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                            },
                                        },
                                    }}
                                    error={!!errors.brand}
                                >
                                    <MenuItem value="" disabled>Select Brand / اختر العلامة التجارية</MenuItem>
                                    {brandsData?.data?.data?.map((brand: any) => (
                                        <MenuItem key={brand._id} value={brand._id}>
                                            {brand.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.brand && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.brand}</span>}
                            </div>
                            <div className="control_group w_50">
                                <label>Model / الطراز</label>
                                <Select
                                    fullWidth
                                    hiddenLabel
                                    value={selectedModel}
                                    onChange={handleModelChange}
                                    displayEmpty
                                    disabled={!selectedBrand}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                            },
                                        },
                                    }}
                                    error={!!errors.model}
                                >
                                    <MenuItem value="" disabled>Select Model / اختر الطراز</MenuItem>
                                    {modelsData?.data?.data?.map((model: any) => (
                                        <MenuItem key={model._id} value={model._id}>
                                            {model.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.model && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.model}</span>}
                            </div>
                            <div className="control_group w_50">
                                <label htmlFor="Category">Category / الفئة</label>
                                <Select
                                    fullWidth
                                    hiddenLabel
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    displayEmpty
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                            },
                                        },
                                    }}
                                    error={!!errors.category}
                                >
                                    <MenuItem value="" disabled>Select Category / اختر الفئة</MenuItem>
                                    {categoriesData?.data?.data?.map((category: any) => (
                                        <MenuItem key={category._id} value={category._id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.category}</span>}
                            </div>



                            {/* Popular */}
                            <div className="control_group flex w_100">
                                <label>Is this Popular Variant? / هل هذا المتغير شائع؟</label>
                                <SwitchToggle
                                    checked={isPopular}
                                    onChange={(e) => setIsPopular(e.target.checked)}
                                />
                            </div>
                        </div>

                        <div className="footer_btn_flex">
                            <Button
                                type="submit"
                                className="btn btn_primary"
                                disabled={isAdding || isUpdating}
                            >
                                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
}
